import React, { useMemo } from 'react'
import moment from 'moment'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  buildYearDaySummaries,
  describeAvailableStaff,
  getShiftStatus,
  getYearDaySource,
  getYearDayStatus,
  isDraftRosterShift,
  shiftSourceGroups,
  yearDayStatusLegend,
  yearDayStatusOrder,
  yearDayStatusStyles,
} from './globalRosterUtils'

const EMPTY_DAY_COLOR = 'rgba(148, 163, 184, 0.16)'
// Ring drawn inside a day square when at least one member of staff has said
// they can work that day (My Availability)
const AVAILABLE_RING = '#059669'

const availableKeyItem = (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: 0.5,
        boxSizing: 'border-box',
        border: `2px solid ${AVAILABLE_RING}`,
        bgcolor: 'transparent',
      }}
    />
    <Typography variant="caption" fontWeight={700}>
      Staff available
    </Typography>
  </Stack>
)

const DayTooltip = ({ day, summary, sourceMode, available }) => (
  <Stack gap={0.25}>
    <Typography variant="caption" fontWeight={800}>
      {day.format('ddd D MMM')} · {summary?.total ?? 0} shift
      {(summary?.total ?? 0) === 1 ? '' : 's'}
    </Typography>
    {summary &&
      (sourceMode
        ? Object.keys(shiftSourceGroups)
            .filter((group) => summary.sources?.[group] > 0)
            .map((group) => (
              <Stack key={group} direction="row" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: 0.5,
                    bgcolor: shiftSourceGroups[group].color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption">
                  {summary.sources[group]} {shiftSourceGroups[group].label.toLowerCase()}
                </Typography>
              </Stack>
            ))
        : yearDayStatusOrder
            .filter((status) => summary.counts[status] > 0)
            .map((status) => (
              <Stack key={status} direction="row" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: 0.5,
                    bgcolor: yearDayStatusStyles[status].color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption">
                  {summary.counts[status]}{' '}
                  {yearDayStatusStyles[status].label.toLowerCase()}
                </Typography>
              </Stack>
            )))}
    {available?.count > 0 && (
      <Stack direction="row" alignItems="flex-start" gap={0.5}>
        <Box
          sx={{
            width: 8,
            height: 8,
            mt: 0.5,
            borderRadius: 0.5,
            boxSizing: 'border-box',
            border: `2px solid ${AVAILABLE_RING}`,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption">
          {available.count} staff available — {describeAvailableStaff(available).replace(/^Available: /, '')}
        </Typography>
      </Stack>
    )}
    <Typography variant="caption" color="grey.400">
      Click to open this week
    </Typography>
  </Stack>
)

const MiniMonth = ({
  monthStart,
  summaries,
  availableStaffByDay,
  sourceMode,
  monthStats,
  todayKey,
  onSelectDay,
  // Rolling "next 12 months" spans two calendar years, so the month heading
  // carries the year
  showYear = false,
}) => {
  const gridStart = monthStart.clone().startOf('week')
  const gridEnd = monthStart.clone().endOf('month').endOf('week')
  const cells = []
  for (
    const cursor = gridStart.clone();
    cursor.isSameOrBefore(gridEnd, 'day');
    cursor.add(1, 'day')
  ) {
    cells.push(cursor.clone())
  }
  const weekdayInitials = cells.slice(0, 7).map((day) => day.format('dd')[0])

  return (
    <Stack gap={0.75}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle2" fontWeight={800}>
          {monthStart.format(showYear ? 'MMMM YYYY' : 'MMMM')}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color={monthStats.uncovered ? 'error.main' : 'text.secondary'}
        >
          {monthStats.total
            ? `${monthStats.total} shift${monthStats.total === 1 ? '' : 's'}${
                monthStats.uncovered ? ` · ${monthStats.uncovered} uncovered` : ''
              }`
            : 'No shifts'}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '3px',
        }}
      >
        {weekdayInitials.map((initial, i) => (
          <Typography
            key={i}
            variant="caption"
            textAlign="center"
            color="text.secondary"
            fontWeight={700}
            sx={{ fontSize: '0.65rem', lineHeight: 1.4 }}
          >
            {initial}
          </Typography>
        ))}
        {cells.map((day) => {
          const key = day.format('YYYY-MM-DD')
          if (!day.isSame(monthStart, 'month')) {
            return <Box key={key} />
          }

          const summary = summaries[key]
          const available = availableStaffByDay[key]
          const marker = sourceMode ? getYearDaySource(summary) : getYearDayStatus(summary)
          const color = marker
            ? sourceMode
              ? shiftSourceGroups[marker].color
              : yearDayStatusStyles[marker].color
            : EMPTY_DAY_COLOR
          const isToday = key === todayKey
          const rings = [
            isToday ? '0 0 0 2px #F57C00' : null,
            available?.count ? `inset 0 0 0 2px ${AVAILABLE_RING}` : null,
          ].filter(Boolean)

          const square = (
            <Box
              onClick={() => onSelectDay(day)}
              sx={{
                aspectRatio: '1 / 1',
                borderRadius: 0.75,
                bgcolor: color,
                color: marker ? '#FFFFFF' : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: marker ? 800 : 500,
                lineHeight: 1,
                cursor: 'pointer',
                boxShadow: rings.length ? rings.join(', ') : 'none',
                transition: 'transform .1s ease',
                '&:hover': { transform: 'scale(1.12)', boxShadow: 2 },
              }}
            >
              {day.format('D')}
            </Box>
          )

          return summary || available ? (
            <Tooltip
              key={key}
              title={
                <DayTooltip
                  day={day}
                  summary={summary}
                  sourceMode={sourceMode}
                  available={available}
                />
              }
            >
              {square}
            </Tooltip>
          ) : (
            <React.Fragment key={key}>{square}</React.Fragment>
          )
        })}
      </Box>
    </Stack>
  )
}

// Twelve months starting at `yearStart` - either a calendar year or the
// rolling "next 12 months" window that begins with the current month.
const RosterYearOverview = ({
  yearStart,
  yearEnd,
  shifts,
  selectedRoster,
  getShiftApplications,
  onSelectDay,
  // 'source' colours days by where the shifts come from; anything else is
  // the coverage-status heatmap
  displayMode = 'staff',
  availableStaffByDay = {},
}) => {
  const todayKey = moment().format('YYYY-MM-DD')
  const sourceMode = displayMode === 'source'
  const rangeStart = moment(yearStart).startOf('month')
  const rangeEnd = yearEnd
    ? moment(yearEnd).endOf('day')
    : rangeStart.clone().add(12, 'months').subtract(1, 'day').endOf('day')
  const spansTwoYears = rangeStart.year() !== rangeEnd.year()

  const summaries = useMemo(
    () =>
      buildYearDaySummaries(
        shifts,
        { start: rangeStart, end: rangeEnd },
        getShiftApplications,
      ),
    [shifts, rangeStart.valueOf(), rangeEnd.valueOf(), getShiftApplications],
  )

  // Month headline counts by shift start (a shift is one shift however many
  // days it spans - the day squares are the place multi-day work repeats)
  const monthStats = useMemo(() => {
    const stats = Array.from({ length: 12 }, () => ({ total: 0, uncovered: 0 }))
    shifts.forEach((shift) => {
      const monthIndex = moment(shift.start)
        .startOf('month')
        .diff(rangeStart, 'months')
      if (monthIndex < 0 || monthIndex > 11) return
      const bucket = stats[monthIndex]
      bucket.total += 1
      if (
        !isDraftRosterShift(shift) &&
        getShiftStatus(shift, getShiftApplications(shift)) === 'uncovered'
      ) {
        bucket.uncovered += 1
      }
    })
    return stats
  }, [shifts, rangeStart.valueOf(), getShiftApplications])

  // Only the source groups actually on the board make it into the key
  const sourceLegend = useMemo(() => {
    const present = new Set()
    Object.values(summaries).forEach((summary) => {
      const marker = getYearDaySource(summary)
      if (marker) present.add(marker)
    })
    return Object.entries(shiftSourceGroups)
      .filter(([id]) => present.has(id))
      .map(([id, item]) => ({ id, ...item }))
  }, [summaries])

  const legend = sourceMode ? sourceLegend : yearDayStatusLegend
  const hasAvailability = Object.keys(availableStaffByDay).length > 0

  const months = Array.from({ length: 12 }, (_, i) =>
    rangeStart.clone().add(i, 'months'),
  )

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={2}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" fontWeight={800} color="text.secondary">
            Key
          </Typography>
          {legend.map((item) => (
            <Stack key={item.id} direction="row" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: item.color,
                }}
              />
              <Typography variant="caption" fontWeight={700}>
                {item.label}
              </Typography>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: EMPTY_DAY_COLOR,
              }}
            />
            <Typography variant="caption" fontWeight={700}>
              No shifts
            </Typography>
          </Stack>
          {availableKeyItem}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {sourceMode
              ? 'Each day is coloured by where its shifts come from.'
              : 'Each day shows its most urgent status.'}{' '}
            A green ring means staff have said they are available. Click a day to
            open that week.
          </Typography>
        </Stack>

        {shifts.length === 0 && !hasAvailability && (
          <Alert severity="info">
            {selectedRoster
              ? 'No shifts found for this period.'
              : 'No shifts found for this period. To get started, create a rota plan with the New button in the control panel above — you can then add shifts to it.'}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            columnGap: 3,
            rowGap: 3,
          }}
        >
          {months.map((monthStart, i) => (
            <MiniMonth
              key={monthStart.format('YYYY-MM')}
              monthStart={monthStart}
              summaries={summaries}
              availableStaffByDay={availableStaffByDay}
              sourceMode={sourceMode}
              monthStats={monthStats[i]}
              todayKey={todayKey}
              onSelectDay={onSelectDay}
              showYear={spansTwoYears}
            />
          ))}
        </Box>
      </Stack>
    </Paper>
  )
}

export default RosterYearOverview
